import React from 'react';

/**
 * GRID / LIST view switch for the event browse surfaces.
 * Design handoff (Aug 31 2026), spec 1 — "the view switch".
 *
 * A single 2px black box with two segments divided by a 2px rule. Active
 * segment inverts to black/white. No transition between modes.
 *
 * Icon-only: the words GRID / LIST cost a whole extra row in the mobile filter
 * bar, and the two shapes read faster than the labels anyway. The label lives
 * on aria-label/title so it's still announced and still hoverable.
 */

// Same line vocabulary as the filter pills: 2px stroke, square caps, mitre joins.
const ICO = {
  width: 16,
  height: 16,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'square',
  strokeLinejoin: 'miter',
};

// Four panes — the 2-up card grid.
const GridIcon = () => (
  <svg {...ICO} aria-hidden="true">
    <rect x="3" y="3" width="7.5" height="7.5" />
    <rect x="13.5" y="3" width="7.5" height="7.5" />
    <rect x="3" y="13.5" width="7.5" height="7.5" />
    <rect x="13.5" y="13.5" width="7.5" height="7.5" />
  </svg>
);

// Full-width rules — one row per event.
const ListIcon = () => (
  <svg {...ICO} aria-hidden="true">
    <path d="M3 5h18M3 12h18M3 19h18" />
  </svg>
);

export default function EventViewSwitch({ value = 'list', onChange, className = '', onDark = false }) {
  const seg = (mode, label, Icon) => {
    const active = value === mode;
    return (
      <button
        key={mode}
        type="button"
        onClick={() => onChange?.(mode)}
        aria-pressed={active}
        aria-label={`${label} view`}
        title={`${label} view`}
        className={`flex h-9 items-center justify-center px-3.5 transition-colors sm:h-10 sm:px-4 ${
          active ? 'bg-black text-white' : 'bg-white text-black hover:bg-black hover:text-white'
        } ${mode === 'list' ? `border-l-2 ${onDark ? 'pr-page-border' : 'border-black'}` : ''}`}
      >
        <Icon />
      </button>
    );
  };

  return (
    <div
      role="group"
      aria-label="Event view"
      // On the page background (lasers) the box takes the page ink; in a white bar it stays black.
      className={`flex flex-shrink-0 border-2 ${onDark ? 'pr-page-border' : 'border-black'} ${className}`}
    >
      {seg('grid', 'Grid', GridIcon)}
      {seg('list', 'List', ListIcon)}
    </div>
  );
}
